<template>
  <div class="flex flex-col w-full h-full bg-gray-50 overflow-y-auto">
    <div v-if="isLoading" class="flex-1 flex items-center justify-center py-16">
      <loader />
    </div>
    <template v-else>
      <!-- Centered max-width container on desktop; full-bleed on mobile -->
      <div class="flex flex-col w-full sm:max-w-2xl sm:mx-auto md:max-w-[unset] md:mx-none lg:ps-8 lg:pe-2 lg:pt-4">
        <!-- Desktop header — on mobile the app header + "+" action cover this -->
        <div class="hidden md:flex items-center justify-between mb-4">
          <div>
            <h2 class="text-2xl font-medium text-gray-800">צוות</h2>
            <p class="text-sm text-surface-500 mt-1">
              {{ canManageOthers
                ? 'אנשי הצוות והסניפים של העסק — שירותים, שעות פעילות וזמינות.'
                : 'הפרופיל שלך — השירותים שאת/ה נותן/ת ושעות הפעילות שלך.' }}
            </p>
          </div>
          <Button
            v-if="canManageOthers"
            :label="activeTab === 'branches' ? 'הוספת סניף' : 'איש צוות חדש'"
            icon="pi pi-plus"
            size="small"
            @click="activeTab === 'branches' ? openAddBranch() : openAddEmployee()"
          />
        </div>
        <!-- Tabs: team / branches (employees only get the team tab, so no picker) -->
        <div v-if="tabs.length > 1" class="flex items-center justify-start gap-2 px-4 md:px-0 pt-4 md:pt-1 pb-3">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium transition-colors"
            :class="activeTab === tab.id ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-gray-600'"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
            <span
              class="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold"
              :class="activeTab === tab.id ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'"
            >{{ tab.count }}</span>
          </button>
        </div>

        <!-- ===== Branches tab ===== -->
        <BranchesTab v-if="activeTab === 'branches'" />

        <!-- ===== Team tab ===== -->
        <div v-else class="flex flex-col px-4 md:px-0 pb-6 pt-2 gap-2">
          <!-- Sectioned by branch (2+ branches with staff) -->
          <template v-if="sectioned">
            <div v-for="branch in activeBranchesWithStaff" :key="branch.id" class="mb-3">
              <div class="flex items-center gap-2 px-0.5 pb-2">
                <div class="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none">
                  <i class="pi pi-map-marker text-xs" />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-[13px] font-semibold truncate">{{ branch.name }}</div>
                  <div v-if="branch.address" class="text-[10px] text-gray-500 truncate">{{ branch.address }}</div>
                </div>
                <span class="text-[11px] text-gray-400 font-medium flex-none">{{ membersInBranch(branch.id).length }} עובדים</span>
              </div>
              <div class="flex flex-col gap-2">
                <MemberRow
                  v-for="m in membersInBranch(branch.id)"
                  :key="branch.id + m.userId"
                  :member="m"
                  :branch-id="branch.id"
                  @click="openMember(m.userId)"
                />
              </div>
            </div>
          </template>

          <!-- Flat active list -->
          <template v-else>
            <div class="flex flex-col md:flex-row md:flex-wrap gap-2">
              <MemberRow
                v-for="m in activeMembers"
                :key="m.userId"
                :member="m"
                @click="openMember(m.userId)"
              />
            </div>
          </template>

          <!-- Pending / needs-setup -->
          <div v-if="pendingMembers.length" class="mt-3">
            <div class="flex items-center gap-2 px-0.5 pb-2">
              <span class="text-[11px] font-semibold text-gray-500">ממתינים להשלמה</span>
              <div class="flex-1 h-px bg-gray-200" />
              <span class="text-[11px] text-gray-400">{{ pendingMembers.length }}</span>
            </div>
            <div class="flex flex-col gap-2">
              <div
                v-for="m in pendingMembers"
                :key="m.userId"
                class="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 cursor-pointer"
                @click="openMember(m.userId)"
              >
                <div
                  class="w-9 h-9 rounded-full flex items-center justify-center font-medium text-[13px] flex-none"
                  :style="avatarStyle(m)"
                >{{ initials(m.fullName) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-[13px] font-medium truncate">{{ m.fullName }}</div>
                  <div class="text-[11px] text-gray-500 truncate">{{ pendingReason(m) }}</div>
                </div>
                <span class="text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full flex-none">בהשלמה</span>
              </div>
            </div>
          </div>

          <p v-if="canManageOthers" class="text-[10.5px] text-gray-400 text-center mt-2 leading-relaxed">
            עובד יכול להשתייך למספר סניפים — קובעים לו שעות בכל סניף מתוך הפרופיל שלו
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { MAIN_BRANCH_ID } from '@torim-app/types';
import { useDrawer } from '@torim-app/shared-components';
import { useAdminAvailabilityStore, useAdminStore } from '@app/stores';
import { useAdminSettingsStore } from '@app/stores/admin-settings';
import { AdminRouteNames } from '@app/router/route-names';
import { trackEvent } from '@app/composables/posthog';
import { useHeaderAction } from '@app/composables/header-action';
import AddBranch from '@app/components/admin/modals/AddBranch.vue';
import AddEmployeeSheet from './AddEmployeeSheet.vue';
import MemberRow from './MemberRow.vue';
import BranchesTab from './BranchesTab.vue';
import { avatarStyle, initials, teamMemberBranchIds } from './team-utils';

type TeamMember = (typeof members)['value'][number];

const router = useRouter();
const adminStore = useAdminStore();
const settingsStore = useAdminSettingsStore();
const adminAvailabilityStore = useAdminAvailabilityStore();
const { openDrawer, closeDrawer } = useDrawer();
const { setHeaderAction } = useHeaderAction();

adminStore.setPage(AdminRouteNames.Team);

const isLoading = ref(true);
const activeTab = ref<'team' | 'branches'>('team');

// The equipment (ציוד) tab is hidden until the tan-booth feature ships —
// EquipmentTab.vue / NewResourceSheet.vue / ResourceProfile.vue are ready and waiting.
// Branches are a business-wide setting, so employees only get the team tab.
const tabs = computed(() => [
  { id: 'team' as const, label: 'צוות', count: members.value.length },
  ...(canManageOthers.value
    ? [{ id: 'branches' as const, label: 'סניפים', count: branchOptions.value.length }]
    : [])
]);

const canManageOthers = computed(() => adminStore.getCanManageOthers);
const hasBranches = computed(() => adminStore.hasBranches);
const branchOptions = computed(() => adminStore.getBranchSelectOptions);

// Roster with contact/role info comes from settings; profileImage merged from providers.
// An employee only ever sees their own row — the page doubles as "my profile" for them.
const members = computed(() => {
  const clients = settingsStore.getSettings?.employees?.clients ?? [];
  const providerImages = new Map(adminStore.getProviders.map(p => [p.userId, p.profileImage]));
  return clients
    .filter(c => !!c.userId && (canManageOthers.value || c.userId === adminStore.getUserId))
    .map(c => ({ ...c, profileImage: providerImages.get(c.userId) }));
});

const isComplete = (m: TeamMember): boolean =>
  m.main || (adminStore.getRegularServices.some(s => (s.providingClients ?? []).includes(m.userId))
    && teamMemberBranchIds(m.userId).length > 0);

const activeMembers = computed(() => members.value.filter(isComplete));
const pendingMembers = computed(() => members.value.filter(m => !isComplete(m)));

// Only offer "add employee" when the plan still has a free seat (mirrors the
// legacy employees screen, which gated adding behind the multi-user allowance).
const seatLimit = computed(() => settingsStore.getSettings?.employees?.numOfUsersAllowed ?? 1);
const canAddEmployee = computed(() => canManageOthers.value && members.value.length < seatLimit.value);

const activeBranchesWithStaff = computed(() =>
  branchOptions.value.filter(b => activeMembers.value.some(m => teamMemberBranchIds(m.userId).includes(b.id)))
);
const sectioned = computed(() => hasBranches.value && activeBranchesWithStaff.value.length > 1);

// A member with no availability tags falls back to the main branch (mirrors the calendar).
const membersInBranch = (branchId: string) => activeMembers.value.filter(m => {
  const ids = teamMemberBranchIds(m.userId);
  if (!ids.length) return branchId === MAIN_BRANCH_ID;
  return ids.includes(branchId);
});

const pendingReason = (m: TeamMember): string => {
  const noServices = !adminStore.getRegularServices.some(s => (s.providingClients ?? []).includes(m.userId));
  const noHours = teamMemberBranchIds(m.userId).length === 0;
  return [noServices && 'ללא שירותים', noHours && 'ללא שעות'].filter(Boolean).join(' · ') || 'ממתין להשלמה';
};

const openMember = (userId: string) => {
  router.push({ name: AdminRouteNames.TeamProfile, params: { id: userId } });
};

const openAddEmployee = () => {
  trackEvent('team_add_employee_clicked');
  openDrawer(
    AddEmployeeSheet,
    { canAdd: canAddEmployee.value, onDone: onEmployeeCreated, onClose: closeDrawer },
    { title: 'איש צוות חדש', fullMobile: false }
  );
};

const onEmployeeCreated = async (userId: string) => {
  closeDrawer();
  await Promise.all([
    adminStore.fetchBusinessData(),
    settingsStore.fetchBusinessSettings(),
    adminAvailabilityStore.fetchBusinessAvailability()
  ]).catch(console.error);
  router.push({ name: AdminRouteNames.TeamProfile, params: { id: userId }, query: { setup: '1' } });
};

const openAddBranch = () => {
  trackEvent('team_add_branch_clicked');
  openDrawer(AddBranch, { onDone: closeDrawer, onClose: closeDrawer }, { title: 'הוספת סניף' });
};

// The header "+" adds whatever the active tab shows.
const updateHeaderAction = () => {
  if (!canManageOthers.value) return;
  if (activeTab.value === 'branches') {
    setHeaderAction({ icon: 'pi-plus', onClick: openAddBranch, ariaLabel: 'הוספת סניף' });
  } else {
    setHeaderAction({ icon: 'pi-plus', onClick: openAddEmployee, ariaLabel: 'הוספת עובד/ת' });
  }
};

watch(activeTab, updateHeaderAction);

onMounted(async () => {
  updateHeaderAction();
  const tasks: Promise<unknown>[] = [];
  if (!settingsStore.getSettings?.employees?.clients) tasks.push(settingsStore.fetchBusinessSettings());
  tasks.push(adminAvailabilityStore.fetchBusinessAvailability());
  await Promise.all(tasks).catch(console.error);
  isLoading.value = false;
});
</script>
